import React from 'react';
import { LOADING_FORM_ARIA_LABEL, LOADING_FORM_MESSAGE } from '../../config/loadingUi.config';

/** viewport: full route / app shell (stable center in the dynamic viewport). panel: flex child below headers. inline: no outer min-height (parent centers, e.g. MSAL overlay). embedded: section/card body (parent supplies min-height). */
export type LoadingSpinnerLayout = 'viewport' | 'panel' | 'inline' | 'embedded';

/** `compact`: smaller dots and caption for narrow rails/cards (e.g. exam workspace file tiles). */
export type LoadingSpinnerSize = 'default' | 'compact';

interface LoadingSpinnerProps {
  message?: string;
  ariaLabel?: string;
  layout?: LoadingSpinnerLayout;
  /** Defaults to `default` (full-size dots and body text). */
  size?: LoadingSpinnerSize;
  className?: string;
}

const sizeClasses: Record<
  LoadingSpinnerSize,
  { dotRow: string; dot: string; caption: string }
> = {
  default: {
    dotRow: 'mb-6 flex gap-2',
    dot: 'h-3 w-3 animate-bounce rounded-full bg-primary motion-reduce:animate-none motion-reduce:opacity-100',
    caption: 'm-0 text-center text-base font-medium text-text-secondary md:text-body-md sm:text-sm',
  },
  compact: {
    dotRow: 'mb-3 flex gap-1.5',
    dot: 'h-2 w-2 animate-bounce rounded-full bg-primary motion-reduce:animate-none motion-reduce:opacity-100',
    caption: 'm-0 text-center text-sm font-medium text-text-secondary',
  },
};

/** Shell padding matches Form Engine Portal `LoadingSpinner` (p-8 / md:p-6 / sm:p-4). */
const layoutShell: Record<LoadingSpinnerLayout, string> = {
  viewport:
    'box-border flex min-h-dvh w-full min-w-0 flex-col items-center justify-center p-8 md:p-6 sm:p-4',
  panel:
    'box-border flex min-h-0 w-full min-w-0 flex-1 flex-col items-center justify-center p-8 md:p-6 sm:p-4',
  inline: 'flex flex-col items-center',
  embedded:
    'box-border flex w-full min-w-0 flex-col items-center justify-center py-8 md:py-6 sm:py-4',
};

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = LOADING_FORM_MESSAGE,
  ariaLabel = LOADING_FORM_ARIA_LABEL,
  layout = 'viewport',
  size = 'default',
  className = '',
}) => {
  const shell = layoutShell[layout];
  const { dotRow, dot, caption } = sizeClasses[size];
  return (
    <div
      className={`${shell}${className ? ` ${className}` : ''}`}
      role="status"
      aria-label={ariaLabel}
    >
      {/* Dot row + caption typography aligned with Form Engine Portal `LoadingSpinner.tsx` */}
      <div className={dotRow} aria-hidden="true">
        <div
          className={`${dot} [animation-delay:theme(animationDelay.bounce-1)]`}
        />
        <div
          className={`${dot} [animation-delay:theme(animationDelay.bounce-2)]`}
        />
        <div className={dot} />
      </div>
      <p className={caption}>{message}</p>
    </div>
  );
};
